"use client";

import styles from './Header.module.css';
import { useTheme } from '@/lib/ThemeContext';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useMobileNav } from '@/lib/ui/MobileNavContext';
import { Bell, Search, X, Sun, Moon, Globe, Menu } from 'lucide-react';
import { UserButton, OrganizationSwitcher, ClerkLoaded, ClerkLoading, SignedIn, SignedOut } from "@clerk/nextjs";
import { format } from 'date-fns';
import { useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

export default function Header({ title }: { title: string }) {
    const { theme, toggleTheme } = useTheme();
    const { language, setLanguage, t, dateLocale } = useLanguage();
    const { toggle } = useMobileNav();

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState<'notif' | 'profile' | 'lang' | null>(null);

    const toggleMenu = (menu: 'notif' | 'profile' | 'lang') => {
        if (activeMenu === menu) setActiveMenu(null);
        else setActiveMenu(menu);
    };

    return (
        <header className={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button className={styles.menuBtn} onClick={toggle}>
                    <Menu size={24} />
                </button>
                <h1 className={styles.title}>{title}</h1>
            </div>

            <div className={styles.actions}>
                <div className={styles.responsiveHide}>
                    <div className={styles.dateDisplay}>
                        {format(new Date(), 'EEEE, MMMM do', { locale: dateLocale })}
                    </div>

                    <div className={styles.divider} />

                    {/* Theme Toggle */}
                    <button className={styles.iconBtn} onClick={toggleTheme}>
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    {/* Language Switcher */}
                    <div style={{ position: 'relative' }}>
                        <button className={styles.iconBtn} onClick={() => toggleMenu('lang')}>
                            <Globe size={20} />
                            <span className={styles.langBadge}>{language.toUpperCase()}</span>
                        </button>

                        {activeMenu === 'lang' && (
                            <div className={styles.dropdown} onMouseLeave={() => setActiveMenu(null)} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {/* ... existing map logic ... */}
                                <div className={styles.dropdownHeader}>Select Language</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('en')}>🇺🇸 English</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('de')}>🇩🇪 Deutsch</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('es')}>🇪🇸 Español</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('fr')}>🇫🇷 Français</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('it')}>🇮🇹 Italiano</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('pt')}>🇵🇹/🇧🇷 Português</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('nl')}>🇳🇱 Nederlands</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('da')}>🇩🇰 Dansk</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('sv')}>🇸🇪 Svenska</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('no')}>🇳🇴 Norsk</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('pl')}>🇵🇱 Polski</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('cs')}>🇨🇿 Čeština</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('hu')}>🇭🇺 Magyar</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('ro')}>🇷🇴 Română</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('bg')}>🇧🇬 Български</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('el')}>🇬🇷 Ελληνικά</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('tr')}>🇹🇷 Türkçe</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('ru')}>🇷🇺 Русский</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('sr')}>🇷🇸 Srpski</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('hi')}>🇮🇳 हिन्दी</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('id')}>🇮🇩 Bahasa Ind</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('zh')}>🇨🇳 中文 (Simp)</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('ja')}>🇯🇵 日本語</div>
                                <div className={styles.dropdownItem} onClick={() => setLanguage('ko')}>🇰🇷 한국어</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search */}
                {isSearchOpen ? (
                    <div className={styles.searchContainer}>
                        <Search size={16} />
                        <input
                            className={styles.searchInput}
                            placeholder={t('header.search')}
                            autoFocus
                            onBlur={() => setIsSearchOpen(false)}
                        />
                        <button className={styles.iconBtn} onClick={() => setIsSearchOpen(false)}>
                            <X size={14} />
                        </button>
                    </div>
                ) : (
                    <button className={styles.iconBtn} onClick={() => setIsSearchOpen(true)}>
                        <Search size={20} />
                    </button>
                )}

                {/* Notifications */}
                <div style={{ position: 'relative' }}>
                    <button
                        className={styles.iconBtn}
                        onClick={() => toggleMenu('notif')}
                    >
                        <Bell size={20} />
                        <span className={styles.badge} />
                    </button>

                    {activeMenu === 'notif' && (
                        <div className={styles.dropdown} onMouseLeave={() => setActiveMenu(null)}>
                            <div className={styles.dropdownHeader}>Notifications</div>
                            <div className={styles.dropdownItem}>
                                <div>
                                    <div>New Booking: H. Muller</div>
                                    <span className={styles.notifTime}>2 mins ago via Booking.com</span>
                                </div>
                            </div>
                            <div className={styles.dropdownItem}>
                                <div>
                                    <div>Rate Parity Alert</div>
                                    <span className={styles.notifTime}>1 hr ago • Expedia is lower</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* User Profile & Org */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ClerkLoading>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '2px solid var(--border-glass)' }} />
                    </ClerkLoading>
                    <ClerkLoaded>
                        <SignedIn>
                            <OrganizationSwitcher
                                hidePersonal
                                afterCreateOrganizationUrl="/dashboard"
                                afterLeaveOrganizationUrl="/dashboard"
                                afterSelectOrganizationUrl="/dashboard"
                                appearance={{
                                    elements: {
                                        rootBox: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        },
                                        organizationSwitcherTrigger: {
                                            color: 'var(--text-primary)'
                                        }
                                    }
                                }}
                            />
                            <UserButton afterSignOutUrl="/sign-in" />
                        </SignedIn>
                        <SignedOut>
                            <Link href="/sign-in" className={styles.signInBtn}>
                                Sign In
                            </Link>
                        </SignedOut>
                    </ClerkLoaded>
                </div>
            </div>
        </header>
    );
}
